import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Play,
  Plus,
  Trash2,
} from "lucide-react";

import { CopyableUrl } from "@/components/CopyableUrl";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Switch } from "@/components/ui/Switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import {
  useBindSiteChannel,
  useChannels,
  useSiteChannels,
  useUnbindSiteChannel,
} from "@/hooks/useChannels";
import {
  useDeleteSite,
  useSite,
  useUpdateSite,
} from "@/hooks/useSites";
import {
  useBulkCreateUrls,
  useCreateUrl,
  useDeleteUrl,
  useRunUrl,
  useUpdateUrl,
  useUrlsPage,
} from "@/hooks/useUrls";
import type {
  HttpMethod,
  IncidentSeverity,
  ScheduleKind,
  UrlEntry,
  UrlEntryBulkCreate,
  UrlEntryBulkError,
  UrlEntryCreate,
} from "@/lib/api";

const METHODS: HttpMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD"];

interface UrlFormState {
  url: string;
  method: HttpMethod;
  expected_status: string;
  content_match: string;
  schedule_kind: ScheduleKind;
  schedule_value: string;
  timeout_seconds: string;
  latency_sla_ms: string;
  retry_count: string;
  check_ssl: boolean;
  headers: string;
  body: string;
  is_active: boolean;
}

const URLS_PAGE_SIZE = 25;

const EMPTY_URL_FORM: UrlFormState = {
  url: "",
  method: "GET",
  expected_status: "200",
  content_match: "",
  schedule_kind: "interval",
  schedule_value: "60s",
  timeout_seconds: "10",
  latency_sla_ms: "",
  retry_count: "3",
  check_ssl: true,
  headers: "{}",
  body: "",
  is_active: true,
};

function fromEntry(u: UrlEntry): UrlFormState {
  return {
    url: u.url,
    method: u.method,
    expected_status: String(u.expected_status),
    content_match: u.content_match ?? "",
    schedule_kind: u.schedule_kind,
    schedule_value: u.schedule_value,
    timeout_seconds: String(u.timeout_seconds),
    latency_sla_ms: u.latency_sla_ms !== null ? String(u.latency_sla_ms) : "",
    retry_count: String(u.retry_count),
    check_ssl: u.check_ssl,
    headers: JSON.stringify(u.headers ?? {}, null, 2),
    body: u.body ?? "",
    is_active: u.is_active,
  };
}

function parseFormToCreate(form: UrlFormState): UrlEntryCreate {
  let headers: Record<string, string> = {};
  const headersRaw = form.headers.trim();
  if (headersRaw.length > 0) {
    const parsed = JSON.parse(headersRaw) as unknown;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      throw new Error("Headers має бути JSON-обʼєктом.");
    }
    headers = parsed as Record<string, string>;
  }
  return {
    url: form.url.trim(),
    method: form.method,
    expected_status: Number(form.expected_status) || 200,
    content_match: form.content_match.trim() || null,
    schedule_kind: form.schedule_kind,
    schedule_value: form.schedule_value.trim(),
    timeout_seconds: Number(form.timeout_seconds) || 10,
    latency_sla_ms:
      form.latency_sla_ms.trim() === ""
        ? null
        : Number(form.latency_sla_ms) || null,
    retry_count: Number(form.retry_count) || 0,
    check_ssl: form.check_ssl,
    headers,
    body: form.body.trim() === "" ? null : form.body,
    is_active: form.is_active,
  };
}

export default function SiteDetailPage(): JSX.Element {
  const params = useParams<{ id: string }>();
  const siteId = Number(params.id);
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: site, isLoading, error } = useSite(siteId);
  const [urlsPage, setUrlsPage] = useState(1);
  const urlsQ = useUrlsPage(siteId, { page: urlsPage, page_size: URLS_PAGE_SIZE });
  const channelsQ = useChannels();
  const bindingsQ = useSiteChannels(siteId);

  const updateSiteMut = useUpdateSite();
  const deleteSiteMut = useDeleteSite();
  const createUrlMut = useCreateUrl();
  const bulkCreateUrlMut = useBulkCreateUrls();
  const updateUrlMut = useUpdateUrl();
  const deleteUrlMut = useDeleteUrl();
  const runUrlMut = useRunUrl();
  const bindMut = useBindSiteChannel();
  const unbindMut = useUnbindSiteChannel();

  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [editingUrl, setEditingUrl] = useState<UrlEntry | null>(null);
  const [urlForm, setUrlForm] = useState<UrlFormState>(EMPTY_URL_FORM);
  const [urlFormError, setUrlFormError] = useState<string | null>(null);
  const [urlFormMode, setUrlFormMode] = useState<"single" | "bulk">("single");
  const [bulkUrlsText, setBulkUrlsText] = useState("");
  const [bulkErrors, setBulkErrors] = useState<UrlEntryBulkError[]>([]);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [siteName, setSiteName] = useState("");
  const [siteDesc, setSiteDesc] = useState("");
  const [siteActive, setSiteActive] = useState(true);
  const [siteInitialized, setSiteInitialized] = useState(false);

  useEffect(() => {
    if (site && !siteInitialized) {
      setSiteName(site.name);
      setSiteDesc(site.description ?? "");
      setSiteActive(site.is_active);
      setSiteInitialized(true);
    }
  }, [site, siteInitialized]);

  // Snap back into range when the page count shrinks (e.g. after deletions).
  useEffect(() => {
    const total = urlsQ.data?.total;
    if (total === undefined) return;
    const pages = Math.max(1, Math.ceil(total / URLS_PAGE_SIZE));
    if (urlsPage > pages) setUrlsPage(pages);
  }, [urlsQ.data?.total, urlsPage]);

  const unboundChannels = useMemo(() => {
    if (!channelsQ.data) return [];
    const boundIds = new Set((bindingsQ.data ?? []).map((b) => b.channel_id));
    return channelsQ.data.filter((c) => !boundIds.has(c.id));
  }, [channelsQ.data, bindingsQ.data]);

  function openCreateUrl(): void {
    setEditingUrl(null);
    setUrlForm(EMPTY_URL_FORM);
    setUrlFormError(null);
    setUrlFormMode("single");
    setBulkUrlsText("");
    setBulkErrors([]);
    setUrlDialogOpen(true);
  }

  function openEditUrl(u: UrlEntry): void {
    setEditingUrl(u);
    setUrlForm(fromEntry(u));
    setUrlFormError(null);
    setUrlFormMode("single");
    setBulkUrlsText("");
    setBulkErrors([]);
    setUrlDialogOpen(true);
  }

  function parseBulkUrls(text: string): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const piece of text.split(/[\s,;]+/)) {
      const u = piece.trim();
      if (!u || seen.has(u)) continue;
      seen.add(u);
      out.push(u);
    }
    return out;
  }

  async function onUrlSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setUrlFormError(null);
    setBulkErrors([]);

    if (!editingUrl && urlFormMode === "bulk") {
      const urls = parseBulkUrls(bulkUrlsText);
      if (urls.length === 0) {
        setUrlFormError("Додайте хоча б один URL.");
        return;
      }
      let defaults: UrlEntryCreate;
      try {
        defaults = parseFormToCreate({ ...urlForm, url: "https://placeholder.local" });
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : "Некоректні дані форми.";
        setUrlFormError(msg);
        return;
      }
      const { url: _ignore, ...rest } = defaults;
      void _ignore;
      const body: UrlEntryBulkCreate = { urls, defaults: rest };
      try {
        const res = await bulkCreateUrlMut.mutateAsync({ siteId, body });
        toast({
          title: `Створено ${res.created.length} з ${urls.length}`,
          description:
            res.errors.length > 0
              ? `Пропущено: ${res.errors.length}`
              : undefined,
          variant: res.errors.length === 0 ? "success" : "default",
        });
        if (res.errors.length > 0) {
          setBulkErrors(res.errors);
        } else {
          setUrlDialogOpen(false);
        }
      } catch (err: unknown) {
        const detail =
          (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail ?? "Не вдалося зберегти.";
        setUrlFormError(detail);
      }
      return;
    }

    let body: UrlEntryCreate;
    try {
      body = parseFormToCreate(urlForm);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Некоректні дані форми.";
      setUrlFormError(msg);
      return;
    }
    try {
      if (editingUrl) {
        await updateUrlMut.mutateAsync({ id: editingUrl.id, body });
        toast({ title: "URL оновлено", variant: "success" });
      } else {
        await createUrlMut.mutateAsync({ siteId, body });
        toast({ title: "URL створено", variant: "success" });
      }
      setUrlDialogOpen(false);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Не вдалося зберегти.";
      setUrlFormError(detail);
    }
  }

  async function onDeleteUrl(id: number): Promise<void> {
    if (!window.confirm("Видалити URL?")) return;
    try {
      await deleteUrlMut.mutateAsync(id);
      toast({ title: "URL видалено", variant: "success" });
    } catch {
      toast({ title: "Помилка видалення", variant: "destructive" });
    }
  }

  async function onRunUrl(id: number): Promise<void> {
    try {
      const res = await runUrlMut.mutateAsync(id);
      toast({
        title: res.ok ? "Перевірка успішна" : "Перевірка не вдалася",
        description:
          res.status_code !== null
            ? `Код ${res.status_code}, ${res.latency_ms ?? "?"} мс`
            : (res.error ?? undefined),
        variant: res.ok ? "success" : "destructive",
      });
    } catch {
      toast({ title: "Помилка запуску", variant: "destructive" });
    }
  }

  async function onSaveSiteSettings(
    e: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    e.preventDefault();
    try {
      await updateSiteMut.mutateAsync({
        id: siteId,
        body: {
          name: siteName,
          description: siteDesc || null,
          is_active: siteActive,
        },
      });
      toast({ title: "Збережено", variant: "success" });
    } catch {
      toast({ title: "Не вдалося зберегти", variant: "destructive" });
    }
  }

  async function onDeleteSite(): Promise<void> {
    try {
      await deleteSiteMut.mutateAsync(siteId);
      toast({ title: "Сайт видалено", variant: "success" });
      navigate("/sites", { replace: true });
    } catch {
      toast({ title: "Не вдалося видалити", variant: "destructive" });
    }
  }

  async function onBindChannel(channelId: number): Promise<void> {
    try {
      await bindMut.mutateAsync({ siteId, channelId });
      toast({ title: "Канал прив'язано", variant: "success" });
    } catch {
      toast({ title: "Не вдалося прив'язати", variant: "destructive" });
    }
  }

  async function onUnbindChannel(channelId: number): Promise<void> {
    try {
      await unbindMut.mutateAsync({ siteId, channelId });
      toast({ title: "Канал відв'язано", variant: "success" });
    } catch {
      toast({ title: "Не вдалося відв'язати", variant: "destructive" });
    }
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Завантаження…</p>;
  }
  if (error || !site) {
    return (
      <p className="text-sm text-destructive">Сайт не знайдено.</p>
    );
  }

  const urlItems = urlsQ.data?.items ?? [];
  const urlTotal = urlsQ.data?.total ?? 0;
  const urlTotalPages = Math.max(1, Math.ceil(urlTotal / URLS_PAGE_SIZE));
  const urlRangeStart = urlTotal === 0 ? 0 : (urlsPage - 1) * URLS_PAGE_SIZE + 1;
  const urlRangeEnd = Math.min(urlsPage * URLS_PAGE_SIZE, urlTotal);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{site.name}</h1>
          {site.description ? (
            <p className="text-sm text-muted-foreground">{site.description}</p>
          ) : null}
        </div>
        <Badge variant={site.is_active ? "success" : "secondary"}>
          {site.is_active ? "Активний" : "Вимкнено"}
        </Badge>
      </div>

      <Tabs defaultValue="urls">
        <TabsList>
          <TabsTrigger value="urls">URL</TabsTrigger>
          <TabsTrigger value="channels">Канали сповіщень</TabsTrigger>
          <TabsTrigger value="settings">Налаштування</TabsTrigger>
        </TabsList>

        <TabsContent value="urls">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>URL для моніторингу</CardTitle>
                <CardDescription>
                  Список ендпоінтів, що перевіряються.
                </CardDescription>
              </div>
              <Button onClick={openCreateUrl}>
                <Plus className="mr-2 h-4 w-4" />
                Додати URL
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead>Метод</TableHead>
                    <TableHead>Очік. код</TableHead>
                    <TableHead>Розклад</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="text-right">Дії</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {urlsQ.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                        Завантаження…
                      </TableCell>
                    </TableRow>
                  ) : urlItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                        Ще немає жодного URL.
                      </TableCell>
                    </TableRow>
                  ) : (
                    urlItems.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="max-w-[280px]">
                          <CopyableUrl url={u.url} className="font-medium" />
                        </TableCell>
                        <TableCell>{u.method}</TableCell>
                        <TableCell>{u.expected_status}</TableCell>
                        <TableCell className="text-xs">
                          {u.schedule_kind}: {u.schedule_value}
                        </TableCell>
                        <TableCell>
                          {u.is_active ? (
                            <Badge variant="success">on</Badge>
                          ) : (
                            <Badge variant="secondary">off</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onRunUrl(u.id)}
                              disabled={runUrlMut.isPending}
                              aria-label="Запустити зараз"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditUrl(u)}
                              aria-label="Редагувати"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onDeleteUrl(u.id)}
                              aria-label="Видалити"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {urlTotal > 0 ? (
                <div className="flex items-center justify-between gap-4 border-t px-2 py-3 text-sm text-muted-foreground">
                  <span>
                    {urlRangeStart}–{urlRangeEnd} з {urlTotal}
                  </span>
                  <div className="flex items-center gap-2">
                    <span>
                      Стор. {urlsPage} / {urlTotalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setUrlsPage((p) => Math.max(1, p - 1))}
                      disabled={urlsPage <= 1 || urlsQ.isFetching}
                      aria-label="Попередня сторінка"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setUrlsPage((p) => Math.min(urlTotalPages, p + 1))
                      }
                      disabled={urlsPage >= urlTotalPages || urlsQ.isFetching}
                      aria-label="Наступна сторінка"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="channels">
          <Card>
            <CardHeader>
              <CardTitle>Канали сповіщень сайту</CardTitle>
              <CardDescription>
                Прив'язані канали — куди надсилаються сповіщення.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {bindingsQ.isLoading ? (
                <p className="text-sm text-muted-foreground">Завантаження…</p>
              ) : !bindingsQ.data || bindingsQ.data.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Жодного каналу не прив'язано.
                </p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {bindingsQ.data.map((b) => (
                    <li
                      key={b.id}
                      className="flex items-center justify-between p-3"
                    >
                      <div>
                        <div className="font-medium">
                          {b.channel?.name ?? `Канал #${b.channel_id}`}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {b.channel?.kind ?? "?"} • мін. рівень:{" "}
                          <SeverityName s={b.min_severity} />
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onUnbindChannel(b.channel_id)}
                      >
                        Відв'язати
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-center gap-2">
                <Select
                  onValueChange={(v) => onBindChannel(Number(v))}
                  value=""
                >
                  <SelectTrigger className="max-w-xs">
                    <SelectValue placeholder="Додати канал…" />
                  </SelectTrigger>
                  <SelectContent>
                    {unboundChannels.length === 0 ? (
                      <SelectItem value="-" disabled>
                        Немає доступних каналів
                      </SelectItem>
                    ) : (
                      unboundChannels.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name} ({c.kind})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Налаштування сайту</CardTitle>
              <CardDescription>
                Загальні параметри та видалення.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSaveSiteSettings} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="set-name">Назва</Label>
                  <Input
                    id="set-name"
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="set-desc">Опис</Label>
                  <Textarea
                    id="set-desc"
                    value={siteDesc}
                    onChange={(e) => setSiteDesc(e.target.value)}
                  />
                </div>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <Label htmlFor="set-active">Активний</Label>
                  <Switch
                    id="set-active"
                    checked={siteActive}
                    onCheckedChange={setSiteActive}
                  />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button type="submit" disabled={updateSiteMut.isPending}>
                    {updateSiteMut.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Збереження…
                      </>
                    ) : (
                      "Зберегти"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Видалити сайт
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* URL dialog */}
      <Dialog open={urlDialogOpen} onOpenChange={setUrlDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingUrl ? "Редагувати URL" : "Додати URL"}
            </DialogTitle>
            <DialogDescription>
              Параметри перевірки ендпоінта.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onUrlSubmit} className="space-y-4">
            {!editingUrl ? (
              <Tabs
                value={urlFormMode}
                onValueChange={(v) => {
                  setUrlFormMode(v as "single" | "bulk");
                  setUrlFormError(null);
                  setBulkErrors([]);
                }}
              >
                <TabsList>
                  <TabsTrigger value="single">Один URL</TabsTrigger>
                  <TabsTrigger value="bulk">Список URL</TabsTrigger>
                </TabsList>
                <TabsContent value="single">
                  <div className="space-y-2">
                    <Label htmlFor="u-url">URL</Label>
                    <Input
                      id="u-url"
                      value={urlForm.url}
                      onChange={(e) =>
                        setUrlForm((f) => ({ ...f, url: e.target.value }))
                      }
                      placeholder="https://example.com/health"
                      required={urlFormMode === "single"}
                    />
                  </div>
                </TabsContent>
                <TabsContent value="bulk">
                  <div className="space-y-2">
                    <Label htmlFor="u-urls-bulk">
                      Список URL (по одному на рядок або через кому)
                    </Label>
                    <Textarea
                      id="u-urls-bulk"
                      value={bulkUrlsText}
                      onChange={(e) => setBulkUrlsText(e.target.value)}
                      rows={6}
                      className="font-mono text-xs"
                      placeholder={
                        "https://example.com/a\nhttps://example.com/b, https://example.com/c"
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Знайдено: {parseBulkUrls(bulkUrlsText).length}. Спільні
                      параметри застосуються до всіх.
                    </p>
                    {bulkErrors.length > 0 ? (
                      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs">
                        <div className="mb-1 font-medium text-destructive">
                          Пропущено ({bulkErrors.length}):
                        </div>
                        <ul className="max-h-40 space-y-1 overflow-auto">
                          {bulkErrors.map((er, i) => (
                            <li key={`${er.url}-${i}`} className="break-all">
                              <span className="font-mono">{er.url}</span> —{" "}
                              {er.error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="u-url">URL</Label>
                <Input
                  id="u-url"
                  value={urlForm.url}
                  onChange={(e) =>
                    setUrlForm((f) => ({ ...f, url: e.target.value }))
                  }
                  placeholder="https://example.com/health"
                  required
                />
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>Метод</Label>
                <Select
                  value={urlForm.method}
                  onValueChange={(v) =>
                    setUrlForm((f) => ({ ...f, method: v as HttpMethod }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="u-status">Очікуваний код</Label>
                <Input
                  id="u-status"
                  type="number"
                  value={urlForm.expected_status}
                  onChange={(e) =>
                    setUrlForm((f) => ({
                      ...f,
                      expected_status: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="u-timeout">Таймаут (с)</Label>
                <Input
                  id="u-timeout"
                  type="number"
                  value={urlForm.timeout_seconds}
                  onChange={(e) =>
                    setUrlForm((f) => ({
                      ...f,
                      timeout_seconds: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>Тип розкладу</Label>
                <Select
                  value={urlForm.schedule_kind}
                  onValueChange={(v) =>
                    setUrlForm((f) => ({
                      ...f,
                      schedule_kind: v as ScheduleKind,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interval">interval</SelectItem>
                    <SelectItem value="cron">cron</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="u-schedule">Значення розкладу</Label>
                <Input
                  id="u-schedule"
                  value={urlForm.schedule_value}
                  onChange={(e) =>
                    setUrlForm((f) => ({
                      ...f,
                      schedule_value: e.target.value,
                    }))
                  }
                  placeholder={
                    urlForm.schedule_kind === "interval"
                      ? "60s / 5m"
                      : "*/5 * * * *"
                  }
                  required
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="u-latency">SLA затримки (мс)</Label>
                <Input
                  id="u-latency"
                  type="number"
                  value={urlForm.latency_sla_ms}
                  onChange={(e) =>
                    setUrlForm((f) => ({
                      ...f,
                      latency_sla_ms: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="u-retry">Спроб</Label>
                <Input
                  id="u-retry"
                  type="number"
                  value={urlForm.retry_count}
                  onChange={(e) =>
                    setUrlForm((f) => ({ ...f, retry_count: e.target.value }))
                  }
                />
              </div>
              <div className="flex flex-col justify-end gap-2">
                <div className="flex items-center justify-between rounded-md border p-3">
                  <Label htmlFor="u-ssl">SSL</Label>
                  <Switch
                    id="u-ssl"
                    checked={urlForm.check_ssl}
                    onCheckedChange={(v) =>
                      setUrlForm((f) => ({ ...f, check_ssl: v }))
                    }
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="u-match">Очікуваний вміст</Label>
              <Input
                id="u-match"
                value={urlForm.content_match}
                onChange={(e) =>
                  setUrlForm((f) => ({
                    ...f,
                    content_match: e.target.value,
                  }))
                }
                placeholder="Підрядок який має бути в тілі відповіді"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="u-headers">Заголовки (JSON)</Label>
              <Textarea
                id="u-headers"
                value={urlForm.headers}
                onChange={(e) =>
                  setUrlForm((f) => ({ ...f, headers: e.target.value }))
                }
                rows={4}
                className="font-mono text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="u-body">Тіло запиту</Label>
              <Textarea
                id="u-body"
                value={urlForm.body}
                onChange={(e) =>
                  setUrlForm((f) => ({ ...f, body: e.target.value }))
                }
                rows={3}
                className="font-mono text-xs"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <Label htmlFor="u-active">Активний</Label>
              <Switch
                id="u-active"
                checked={urlForm.is_active}
                onCheckedChange={(v) =>
                  setUrlForm((f) => ({ ...f, is_active: v }))
                }
              />
            </div>
            {urlFormError ? (
              <p className="text-sm text-destructive">{urlFormError}</p>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setUrlDialogOpen(false)}
              >
                Скасувати
              </Button>
              <Button
                type="submit"
                disabled={
                  createUrlMut.isPending ||
                  updateUrlMut.isPending ||
                  bulkCreateUrlMut.isPending
                }
              >
                {createUrlMut.isPending ||
                updateUrlMut.isPending ||
                bulkCreateUrlMut.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Збереження…
                  </>
                ) : editingUrl ? (
                  "Зберегти"
                ) : urlFormMode === "bulk" ? (
                  "Створити список"
                ) : (
                  "Створити"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete site confirm */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Видалити сайт?</DialogTitle>
            <DialogDescription>
              Усі URL, історія перевірок та прив'язки каналів буде видалено.
              Цю дію не можна скасувати.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Скасувати
            </Button>
            <Button
              variant="destructive"
              onClick={onDeleteSite}
              disabled={deleteSiteMut.isPending}
            >
              Видалити
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SeverityName({ s }: { s: IncidentSeverity }): JSX.Element {
  const map: Record<IncidentSeverity, string> = {
    info: "інформація",
    warning: "попередження",
    critical: "критично",
  };
  return <span>{map[s]}</span>;
}
